import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Empty } from 'antd';

import { useTypedSelector } from '../../../../../hooks/useTypedSelector';
import Loading from '../Loading';
import { getSeriesAndLabels } from '../utils';
import EChart from '../../../EChart';


const errorMessage = 'Нет данных, соответствующих условиям отчёта';

const COHORT_COLORS = [
    '#119CFF',
    '#7700FF',
    '#BD07FF',
    '#FF05D1',
    '#FF006E',
    '#FF1C1C',
    '#FF6A07',
    '#FFB711',
    '#FBFF19',
    '#A8FF11',
    '#00FF43',
    '#0FFFBB',
    '#07EEFF',
    '#0751FF',
    '#959299',
    '#4C00A3',
    '#6D0493',
    '#8E0275',
    '#7C0036',
    '#A01111',
    '#CE4400',
    '#B77D00',
    '#A0A300',
    '#6CA00B',
    '#00BC2F',
    '#0BBF8C',
    '#049BA5',
    '#0A6099',
    '#043199',
    '#4D4B4F',
];

// Пороговые значения для оптимизации
const LARGE_DATA_THRESHOLD = 1000;
const MANY_LABELS_THRESHOLD = 50;
const MANY_SERIES_THRESHOLD = 20;
const MAX_TOOLTIP_ITEMS = 10;

const ChartUniversal = ({ loadingChart }) => {
    const { tableData } = useTypedSelector((state) => state.table);

    const [ series, setSeries ] = useState([]);
    const [ labels, setLabels ] = useState([]);
    
    useEffect(() => {
        if (tableData.data && tableData.data.length > 0) {
            const { newSeries, newLabels } = getSeriesAndLabels(tableData);

            setLabels(newLabels);
            setSeries(newSeries);
        }
    }, [ tableData ]);

    // Мемоизированный форматтер для tooltip
    const tooltipFormatter = useCallback((params) => {
        if (!params || !params.length) return '';
        
        // Фильтруем только ненулевые значения и сортируем по убыванию
        const filteredParams = params
            .filter(p => p.value && p.value > 0)
            .sort((a, b) => (b.value || 0) - (a.value || 0));
        
        if (filteredParams.length === 0) return '';
        
        let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`;
        
        // При большом количестве серий показываем только топ-N
        const itemsToShow = filteredParams.length > MAX_TOOLTIP_ITEMS 
            ? filteredParams.slice(0, MAX_TOOLTIP_ITEMS) 
            : filteredParams;
        
        itemsToShow.forEach((param) => {
            const value = param.value?.toLocaleString('ru-RU') || 0;
            result += `<div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 2px; background: ${param.color}; margin-right: 8px;"></span>
                <span>${param.seriesName}: ${value}</span>
            </div>`;
        });
        
        // Показываем сколько ещё скрыто
        if (filteredParams.length > MAX_TOOLTIP_ITEMS) {
            const hiddenCount = filteredParams.length - MAX_TOOLTIP_ITEMS;
            result += `<div style="color: #999; margin-top: 4px; font-size: 11px;">... и ещё ${hiddenCount}</div>`;
        }
        
        return result;
    }, []);

    const echartsOption = useMemo(() => {
        if (series.length === 0 || labels.length === 0) {
            return null;
        }

        const totalDataPoints = series.length * labels.length;
        const isLargeData = totalDataPoints > LARGE_DATA_THRESHOLD;
        const hasManyLabels = labels.length > MANY_LABELS_THRESHOLD;
        const hasManySeries = series.length > MANY_SERIES_THRESHOLD;

        const echartsSeriesData = series.map((s, index) => ({
            name: s.name,
            type: 'bar',
            stack: 'total',
            data: s.data,
            itemStyle: {
                color: COHORT_COLORS[index % COHORT_COLORS.length],
            },
            // Оптимизация для больших данных
            large: isLargeData,
            largeThreshold: LARGE_DATA_THRESHOLD,
            // Отключаем эффекты при наведении для большого количества данных
            emphasis: isLargeData ? { disabled: true } : undefined,
        }));

        // Определяем поворот меток оси X
        const hasDateFormat = labels[0]?.split('/').length - 1 === 2;
        const xAxisRotate = hasManyLabels ? 45 : (hasDateFormat ? 45 : 0);

        return {
            animation: false,
            tooltip: {
                trigger: 'axis',
                confine: true,
                appendToBody: true,
                // enterable + скролл: при множестве серий tooltip можно навести и прокрутить
                enterable: true,
                extraCssText: 'max-height:260px;overflow:auto;',
                axisPointer: {
                    type: 'shadow',
                },
                formatter: tooltipFormatter,
            },
            legend: {
                show: true,
                type: 'scroll',
                orient: 'vertical',
                right: 10,
                top: 40,
                bottom: hasManyLabels ? 60 : 20,
                // Пагинация для большого количества серий
                pageButtonItemGap: 5,
                pageButtonGap: 5,
                pageIconSize: 12,
            },
            grid: {
                left: '3%',
                right: '15%',
                bottom: hasManyLabels ? '15%' : '3%',
                top: '10%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: {
                    rotate: xAxisRotate,
                    fontSize: hasManyLabels ? 7 : 8,
                    // Автоматический интервал при большом количестве меток
                    interval: hasManyLabels ? 'auto' : 0,
                    // Ограничиваем длину метки
                    formatter: (value) => {
                        if (value && value.length > 15) {
                            return value.substring(0, 12) + '...';
                        }
                        return value;
                    },
                },
            },
            yAxis: {
                type: 'value',
            },
            // DataZoom для навигации при большом количестве данных
            dataZoom: hasManyLabels ? [
                {
                    type: 'inside',
                    start: 0,
                    end: Math.min(100, (MANY_LABELS_THRESHOLD / labels.length) * 100),
                    zoomOnMouseWheel: true,
                    moveOnMouseMove: true,
                },
                {
                    type: 'slider',
                    start: 0,
                    end: Math.min(100, (MANY_LABELS_THRESHOLD / labels.length) * 100),
                    bottom: 5,
                    height: 20,
                    handleSize: '80%',
                },
            ] : undefined,
            series: echartsSeriesData,
        };
    }, [series, labels, tooltipFormatter]);

    return (
        <div className="universal-chart" id="universal_chart" fullHeight>
            <Loading loading={loadingChart}>
                {
                    tableData.data?.length === 0 && (tableData.total && Object.keys(tableData.total)?.length === 0) ?
                    <div style={{ width: 1000, height: 370 }}>
                        <div className="table-overlay">
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={errorMessage} />
                        </div>
                    </div>
                    :
                    echartsOption && (
                        <EChart
                            option={echartsOption}
                            style={{ height: 370, width: '100%' }}
                            lazyLoad={false}
                            notMerge
                        />
                    )
                }
            </Loading>

        </div>
    );
};

export default memo(ChartUniversal);
